import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { Capacitor } from "@capacitor/core";
import { FirebaseError } from "firebase/app";
import {
	AuthError,
	OAuthProvider,
	User,
	linkWithCredential,
	linkWithPopup,
	reauthenticateWithPopup,
	signInWithCustomToken,
	signInWithPopup,
} from "firebase/auth";
import { backendApi } from "../../../../common/apiService";
import { auth, microsoftAuthProvider } from "../../../../firebaseConfig";

const NATIVE_MICROSOFT_OPTIONS = {
	customParameters: [{ key: "prompt", value: "select_account" }],
};

/* Native errors come as "auth/account-exists-with-different-credential" too, but keep it robust to casing */
export function getAuthErrorCode(err: any): string {
	return typeof err?.code === "string" ? err.code.toLowerCase().replace(/_/g, "-") : "";
}

/**
 * Sign in with the Firebase "microsoft.com" provider.
 *
 * Web: signInWithPopup on the Firebase JS SDK.
 * iOS/Android: the JS SDK cannot sign in with a Microsoft credential (signInWithCredential is not
 * supported for microsoft.com), so the user signs in on the native layer and the backend exchanges
 * the native Firebase ID token for a custom token of the same uid.
 *
 * On "auth/account-exists-with-different-credential" the error is rethrown: the caller can keep
 * OAuthProvider.credentialFromError(err) and link it after the user signs in with the other provider.
 */
export async function signInWithMicrosoft(): Promise<User> {
	if (Capacitor.isNativePlatform()) return signInWithMicrosoftNative();

	try {
		const result = await signInWithPopup(auth, microsoftAuthProvider);
		return result.user;
	} catch (err) {
		if (getAuthErrorCode(err) === "auth/account-exists-with-different-credential") {
			const user = await signInLegacyMicrosoftAccount(err as AuthError);
			if (user) return user;
		}
		throw err;
	}
}

async function signInWithMicrosoftNative(): Promise<User> {
	await FirebaseAuthentication.signInWithMicrosoft(NATIVE_MICROSOFT_OPTIONS);
	try {
		const { token } = await FirebaseAuthentication.getIdToken();
		const response = await backendApi.post("/microsoftCustomToken", { firebase_id_token: token });
		const { user } = await signInWithCustomToken(auth, response.data.firebaseToken);
		return user;
	} finally {
		// From here on the session lives in the JS SDK, like the other providers
		await FirebaseAuthentication.signOut();
	}
}

/**
 * Accounts created by the previous Microsoft flow (PKCE + backend custom token) have no "microsoft.com"
 * provider, so Firebase answers account-exists. The backend verifies the Microsoft ID token and returns
 * a custom token for the existing account, then the pending credential is linked so the next sign-in
 * goes straight through Firebase.
 */
async function signInLegacyMicrosoftAccount(error: AuthError): Promise<User | null> {
	const pendingCred = OAuthProvider.credentialFromError(error);
	if (!pendingCred?.idToken) return null;

	try {
		const response = await backendApi.post("/microsoftCustomToken", { id_token: pendingCred.idToken });
		const { user } = await signInWithCustomToken(auth, response.data.firebaseToken);
		await linkWithCredential(user, pendingCred).catch((err) => console.error("[Microsoft] error linking legacy account:", err));
		return user;
	} catch (err) {
		console.error("[Microsoft] legacy account sign-in failed:", err);
		return null;
	}
}

/**
 * Confirm the identity of the signed-in Microsoft user (e.g. before deleting the account) without
 * switching the current session. Throws "auth/user-mismatch" if a different account is used.
 */
export async function reauthenticateWithMicrosoft(): Promise<void> {
	const currentUser = auth.currentUser;
	if (!currentUser) throw new FirebaseError("auth/no-current-user", "No signed-in user");

	if (Capacitor.isNativePlatform()) {
		const result = await FirebaseAuthentication.signInWithMicrosoft(NATIVE_MICROSOFT_OPTIONS);
		if (result.user?.uid !== currentUser.uid) {
			// Do not leave behind an empty account created by picking a different Microsoft account
			if (result.additionalUserInfo?.isNewUser) await FirebaseAuthentication.deleteUser();
			else await FirebaseAuthentication.signOut();
			throw new FirebaseError("auth/user-mismatch", "Different Microsoft account");
		}
		await FirebaseAuthentication.signOut();
		return;
	}

	if (currentUser.providerData.some((provider) => provider.providerId === "microsoft.com")) {
		await reauthenticateWithPopup(currentUser, microsoftAuthProvider);
	} else {
		// Account from the previous flow: linking confirms the Microsoft login and migrates it
		await linkWithPopup(currentUser, microsoftAuthProvider).catch((err) => {
			if (getAuthErrorCode(err) === "auth/credential-already-in-use") throw new FirebaseError("auth/user-mismatch", "Different Microsoft account");
			throw err;
		});
	}
}
